/**
 * Global: injects MP4/MOV / YouTube / Vimeo into any element with `data-video`.
 * Skips `.text-bg-video-clip` and anything inside it (handled by textBgVideo.js).
 * Inner page banner: only inits `[data-video]` when `.inner-page-banner__bg` also has `.inner-page-banner__bg--video` (image-only mode skips embed load).
 *
 * Optional: data-poster — used for MP4/MOV <video> poster only.
 */

import { createVideoPlayer, isDirectVideoFile } from "./videoPlayer.js";

const LOADED_CLASS = "data-video--loaded";
/** Set synchronously on mount: `LOADED_CLASS` only lands after the embed loads, so it can't guard re-entry. */
const INIT_ATTR = "data-video-mounted";

/**
 * @param {Element} container
 * @returns {HTMLElement | null}
 */
export function initDataVideoElement(container) {
  if (!(container instanceof Element)) return null;
  if (container.hasAttribute(INIT_ATTR) || container.classList.contains(LOADED_CLASS)) {
    return container.querySelector("video, iframe");
  }
  if (container.closest(".text-bg-video-clip")) return null;
  if (container.closest(".full-width-image-video")) return null;

  const url = container.getAttribute("data-video");
  const poster = container.getAttribute("data-poster") ?? "";
  container.setAttribute(INIT_ATTR, "");

  const { el, type } = createVideoPlayer(url, container, {
    onReady: () => container.classList.add(LOADED_CLASS),
  });

  if (!el) return null;

  if (isDirectVideoFile(type) && poster) {
    el.setAttribute("poster", poster);
  }

  return el;
}

export function initDataVideoElements() {
  document.querySelectorAll("[data-video]").forEach((container) => {
    initDataVideoElement(container);
  });
}
